import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, ExternalLink, X, ArrowLeft } from 'lucide-react';
import { useThemeLogos } from "@/hooks/useThemeLogos";
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';

import { useSideRail } from '@/contexts/SideRailContext';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  useAskHobbes,
  formatEvidenceDate,
  getBuyingIntentBadgeClassName,
  cleanResponseContent,
} from '@/hooks/useAskHobbes';
import { prepareResponseContent } from '@/lib/orgAskStream';
import {
  getQualificationBadgeVariant,
  getQualificationBadgeClassName,
  getQualificationBadgeLabel,
} from '@/lib/badgeUtils';
import type { OrgAskEvidenceItem, OrgAskSessionEvidence } from '@/lib/api';

interface AskHobbesChatProps {
  mode?: 'rail' | 'page';
}

const renderEvidenceCard = (item: OrgAskEvidenceItem, index: number) => {
  if (item.type !== 'session') {
    const sessionIds = Array.isArray(item.sessions) ? item.sessions : [];
    return (
      <div
        key={`aggregate-${index}`}
        className="rounded-lg border border-default card-bg-light px-4 py-3 text-sm text-body"
      >
        <p>{item.content}</p>
        {sessionIds.length > 0 && (
          <div className="mt-3 space-y-2">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-subtle">
              Related sessions
            </div>
            <div className="flex flex-wrap gap-2 text-xs text-black">
              {sessionIds.slice(0, 5).map((sessionId) => (
                <Button key={sessionId} variant="outline" size="sm" asChild>
                  <Link to={`/analytics/sessions?sessionId=${encodeURIComponent(sessionId)}`}>
                    {sessionId.slice(0, 8)}
                  </Link>
                </Button>
              ))}
              {sessionIds.length > 5 && (
                <span className="text-[11px] font-medium text-subtle">
                  +{sessionIds.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  const sessionItem: OrgAskSessionEvidence = item;
  const sessionLink = `/analytics/sessions?sessionId=${encodeURIComponent(sessionItem.session)}`;
  const startedAt = formatEvidenceDate(sessionItem.started_at || sessionItem.created_at);

  return (
    <div
      key={sessionItem.session}
      className="rounded-lg border card-border card-bg-light p-4 space-y-3"
    >
      {/* Header with email and badges */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2 flex-1">
          {sessionItem.email && (
            <div className="font-semibold text-heading">{sessionItem.email}</div>
          )}
          <p className="text-sm text-body">{sessionItem.content}</p>
          <div className="flex flex-wrap items-center gap-2">
            {(sessionItem.qualification_status || sessionItem.booked_status) && (
              <Badge
                variant={getQualificationBadgeVariant(sessionItem.qualification_status, sessionItem.booked_status)}
                className={`capitalize ${getQualificationBadgeClassName(sessionItem.qualification_status, sessionItem.booked_status)}`}
              >
                {getQualificationBadgeLabel(sessionItem.qualification_status, sessionItem.booked_status)}
              </Badge>
            )}
            {sessionItem.buying_intent && (
              <Badge
                variant="outline"
                className={`capitalize ${getBuyingIntentBadgeClassName(sessionItem.buying_intent)}`}
              >
                {sessionItem.buying_intent} intent
              </Badge>
            )}
            {sessionItem.sentiment && (
              <Badge variant="outline" className="capitalize">
                {sessionItem.sentiment}
              </Badge>
            )}
            {startedAt && (
              <span className="text-xs text-black">{startedAt}</span>
            )}
          </div>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to={sessionLink} className="flex items-center gap-2 shrink-0">
            View Details
            <ExternalLink className="h-3 w-3" />
          </Link>
        </Button>
      </div>

      {/* Topics Discussed */}
      {sessionItem.topics_discussed && sessionItem.topics_discussed.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-black mb-2">Topics Discussed</div>
          <div className="flex flex-wrap gap-2">
            {sessionItem.topics_discussed.map((topic, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {topic}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Objections */}
      {sessionItem.objection_types && sessionItem.objection_types.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-black mb-2">Objections</div>
          <div className="flex flex-wrap gap-2">
            {sessionItem.objection_types.map((objection, idx) => (
              <Badge key={idx} variant="outline" className="text-xs text-red-700 border-red-200">
                {objection}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Next Steps */}
      {sessionItem.next_steps && sessionItem.next_steps.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-black mb-2">Next Steps</div>
          <ul className="space-y-1 text-sm text-body">
            {sessionItem.next_steps.map((step, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span>{step}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const AskHobbesChat: React.FC<AskHobbesChatProps> = ({ mode = 'rail' }) => {
  const sideRailContext = useSideRail();
  const { entries, isSubmitting, handleSubmit } = useAskHobbes();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [prompt, setPrompt] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const handledPromptRef = useRef<string | null>(null);
  const { favicon: HobbesIcon } = useThemeLogos();
  const isMobile = useIsMobile();

  const isRailMode = mode === 'rail';
  const isPageMode = mode === 'page';

  const handleOpenFullScreen = () => {
    if (isRailMode && sideRailContext) {
      sideRailContext.closeSideRail();
    }
    navigate('/ask-hobbes');
  };

  const handleBack = () => {
    navigate('/');
  };

  // Handle initial prompt from context (rail mode)
  useEffect(() => {
    if (isRailMode && sideRailContext) {
      const { isOpen, initialPrompt, setInitialPrompt } = sideRailContext;
      if (isOpen && initialPrompt && initialPrompt !== handledPromptRef.current) {
        handledPromptRef.current = initialPrompt;
        handleSubmit(initialPrompt);
        setInitialPrompt('');
      }
    }
  }, [isRailMode, sideRailContext, handleSubmit]);

  // Handle URL prompt param (page mode)
  useEffect(() => {
    if (isPageMode) {
      const urlPrompt = searchParams.get('prompt');
      if (urlPrompt && urlPrompt !== handledPromptRef.current) {
        handledPromptRef.current = urlPrompt;
        handleSubmit(urlPrompt);
        // Clear the URL param after handling
        setSearchParams({}, { replace: true });
      }
    }
  }, [isPageMode, searchParams, setSearchParams, handleSubmit]);

  // Scroll to bottom when new entries arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries]);

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (prompt.trim()) {
      handleSubmit(prompt.trim());
      setPrompt('');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (prompt.trim()) {
        handleSubmit(prompt.trim());
        setPrompt('');
      }
    }
  };

  // Container classes based on mode
  const baseClasses = "border card-border rounded-xl card-bg flex flex-col overflow-hidden";
  const containerClasses = isPageMode
    ? `w-full ${isMobile ? "h-full" : "h-[calc(100vh-2rem)]"} ${baseClasses}`
    : `${isMobile ? "w-full h-full" : "w-[480px] h-[calc(100vh-2rem)]"} ${baseClasses}`;

  const content = (
    <>
      {/* Header */}
      <div className="px-6 py-4 border-b card-border shrink-0 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isPageMode && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-8 w-8 mr-1"
              title="Back to Dashboard"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <img src={HobbesIcon} alt="Hobbes" className="h-5 w-5" />
          <h2 className="font-semibold text-foreground">Ask Hobbes</h2>
        </div>
        {isRailMode && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleOpenFullScreen}
              className="h-8 w-8"
              title="Open in new tab"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => sideRailContext?.closeSideRail()}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Chat Messages Area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4">
        {entries.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center text-muted-foreground">
            <img src={HobbesIcon} alt="Hobbes" className="mb-3 h-8 w-8 opacity-50" />
            <p className="max-w-sm text-sm">
              Ask Hobbes anything about your sessions, prospects, or sales performance.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {entries.map((entry) => (
              <div key={entry.id} className="space-y-3">
                {/* User message */}
                <div className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm">
                    {entry.prompt}
                  </div>
                </div>

                {/* Hobbes response */}
                <div className="flex justify-start">
                  <div className="max-w-[85%] space-y-3 text-sm">
                    {/* Loading state */}
                    {entry.status === 'loading' && !entry.progress && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Hobbes is thinking...</span>
                      </div>
                    )}

                    {/* Progress state */}
                    {entry.progress && (
                      <div className="flex items-start gap-2 text-xs">
                        <Loader2 className="mt-0.5 h-3.5 w-3.5 animate-spin text-muted-foreground" />
                        <div>
                          <div className="font-medium text-foreground">{entry.progress.headline}</div>
                          {entry.progress.subline && (
                            <div className="mt-1 italic text-muted-foreground">{entry.progress.subline}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Error state */}
                    {entry.status === 'error' && (
                      <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        {entry.errorMessage ?? 'Something went wrong.'}
                      </div>
                    )}

                    {/* Success state */}
                    {entry.status === 'success' && (
                      <div className="prose prose-sm prose-zinc max-w-none leading-relaxed dark:prose-invert">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            a: ({ node, href, ...props }) =>
                              href && href.startsWith('/') ? (
                                <Link to={href} {...props} />
                              ) : (
                                <a href={href} target="_blank" rel="noopener noreferrer" {...props} />
                              ),
                          }}
                        >
                          {cleanResponseContent(prepareResponseContent(
                            entry.response?.content,
                            (entry.response?.evidence?.length ?? 0) > 0
                          )) || 'No answer returned.'}
                        </ReactMarkdown>
                      </div>
                    )}

                    {/* Evidence cards */}
                    {entry.status === 'success' && (entry.response?.evidence?.length ?? 0) > 0 && (
                      <div className="space-y-2 border-t border-default pt-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Evidence
                        </div>
                        {entry.response?.evidence?.map((item, index) => renderEvidenceCard(item, index))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chat Input */}
      <div className="border-t card-border p-4 shrink-0">
        <form onSubmit={handleFormSubmit} className="rounded-xl border card-border bg-card p-3">
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Hobbes anything..."
            disabled={isSubmitting}
            className="min-h-[60px] resize-none border-0 bg-transparent px-0 text-sm shadow-none focus-visible:ring-0"
          />
          <div className="mt-2 flex items-center justify-end">
            <Button
              type="submit"
              size="sm"
              disabled={isSubmitting || !prompt.trim()}
              className="gap-2"
            >
              {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Send
            </Button>
          </div>
        </form>
      </div>
    </>
  );

  // In page mode, render without animation
  if (isPageMode) {
    return (
      <div className={containerClasses}>
        {content}
      </div>
    );
  }

  // In rail mode, render with slide animation
  return (
    <motion.div
      key="ask-hobbes-chat"
      initial={{ x: "100%", opacity: 0 }}
      animate={{
        x: 0,
        opacity: 1,
        transition: {
          type: "tween",
          duration: 0.5,
          ease: [0.25, 0.1, 0.25, 1]
        }
      }}
      exit={{
        x: "100%",
        opacity: 0,
        transition: {
          type: "tween",
          duration: 0.3,
          ease: [0.25, 0.1, 0.25, 1]
        }
      }}
      className={containerClasses}
    >
      {content}
    </motion.div>
  );
};
